import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../i18n'
import { setLanguage, LANGUAGES } from '../i18n'
import { useAuth } from '../context'
import OfflineBanner from '../components/OfflineBanner'
import { PortalPage } from '../components/portal/PortalPage'
import { TRANSITION_PRESS, TRANSITION_UI } from '../lib/motion'

const s: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: '100vh',
    background: '#0a0f1a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logo: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #7b93db, #44c9ab)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
  },
  greeting: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '55%',
  },
  roleBadge: {
    padding: '2px 8px',
    borderRadius: 6,
    background: 'rgba(123, 147, 219, 0.15)',
    border: '1px solid rgba(123, 147, 219, 0.3)',
    color: '#7b93db',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  actions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  iconBtn: {
    width: 38,
    height: 38,
    borderRadius: 10,
    border: 'none',
    background: 'rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.6)',
    position: 'relative' as const,
    transition: TRANSITION_PRESS,
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflowX: 'hidden' as const,
    overflowY: 'auto' as const,
  },
  bottomBar: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    minHeight: 68,
    background: '#0d1320',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: '0 8px',
    zIndex: 50,
  },
  tab: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 3,
    padding: '8px 16px',
    borderRadius: 12,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: TRANSITION_UI,
    minWidth: 60,
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.3,
  },
  langDropdown: {
    position: 'absolute' as const,
    top: 48,
    right: 0,
    background: '#151c2c',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: 6,
    zIndex: 200,
    minWidth: 150,
    maxHeight: '70vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  langItem: {
    padding: '10px 14px',
    borderRadius: 8,
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left' as const,
    display: 'block',
  },
  langItemActive: {
    background: 'rgba(123, 147, 219, 0.15)',
    color: '#7b93db',
  },
}

const HomeIcon = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? '#7b93db' : 'none'} stroke={active ? '#7b93db' : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
)

const TasksIcon = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#7b93db' : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
)

const HistoryIcon = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#7b93db' : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
)

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

const tabs = [
  { key: 'home', path: '/buyer/home', labelKey: 'tab_home', Icon: HomeIcon },
  { key: 'tasks', path: '/buyer/tasks', labelKey: 'tab_tasks', Icon: TasksIcon },
  { key: 'history', path: '/buyer/history', labelKey: 'tab_history', Icon: HistoryIcon },
]

export function BuyerApp({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation()
  const { profile, logout, isImpersonating, exitImpersonation } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showLang, setShowLang] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowLang(false)
      }
    }
    if (showLang) {
      document.addEventListener('pointerdown', handleClick)
      document.addEventListener('click', handleClick)
    }
    return () => {
      document.removeEventListener('pointerdown', handleClick)
      document.removeEventListener('click', handleClick)
    }
  }, [showLang])

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const handleExitViewAs = () => {
    exitImpersonation()
    navigate('/users')
  }

  const handleLangChange = (code: string) => {
    void (async () => {
      await setLanguage(code)
      setShowLang(false)
    })()
  }

  const isLangActive = (code: string) =>
    (i18n.language || 'en').toLowerCase().startsWith(code.toLowerCase())

  const name = profile?.full_name?.split(' ')[0] || t('buyer', 'Evaluator')

  const activeTab = tabs.find(tab => location.pathname.startsWith(tab.path))?.key
    || (location.pathname.startsWith('/buyer/evaluate') ? 'tasks' : 'home')

  return (
    <div className="portal-shell" style={s.shell}>
      <OfflineBanner />
      <div className="portal-app-header" style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.logo}>RS</div>
          <span style={s.greeting}>{name}</span>
          <span style={s.roleBadge}>{t('chi_role', 'CHI')}</span>
          {isImpersonating && (
            <span style={{ ...s.roleBadge, background: 'rgba(239, 183, 118, 0.2)', color: '#efb776' }}>
              {t('viewing_as', 'Viewing As')}
            </span>
          )}
        </div>
        <div style={s.actions}>
          {isImpersonating && (
            <button
              type="button"
              style={{ ...s.iconBtn, width: 'auto', padding: '0 10px', fontSize: 12, fontWeight: 600 }}
              onClick={handleExitViewAs}
            >
              {t('exit_view_as', 'Exit View As')}
            </button>
          )}
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button type="button" style={s.iconBtn} onClick={() => setShowLang(!showLang)} aria-label={t('lang_select', 'Language')}>
              <GlobeIcon />
            </button>
            {showLang && (
              <div
                style={s.langDropdown}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {LANGUAGES.map(lang => (
                  <button
                    type="button"
                    key={lang.code}
                    style={{
                      ...s.langItem,
                      ...(isLangActive(lang.code) ? s.langItemActive : {}),
                    }}
                    onClick={() => handleLangChange(lang.code)}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" style={s.iconBtn} onClick={handleLogout} aria-label={t('home_logout', 'Logout')}>
            <LogoutIcon />
          </button>
        </div>
      </div>

      <div className="portal-content" style={s.content}>
        <PortalPage>{children}</PortalPage>
      </div>

      <div className="portal-tab-bar" style={s.bottomBar}>
        {tabs.map(({ key, path, labelKey, Icon }) => {
          const isActive = activeTab === key
          return (
            <button
              type="button"
              key={key}
              style={{
                ...s.tab,
                background: isActive ? 'rgba(123, 147, 219, 0.1)' : 'transparent',
              }}
              onClick={() => navigate(path)}
            >
              <Icon active={isActive} />
              <span style={{
                ...s.tabLabel,
                color: isActive ? '#7b93db' : 'rgba(255,255,255,0.35)',
              }}>
                {t(labelKey)}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
